import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { DigitalClock } from '@mui/x-date-pickers/DigitalClock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InputAdornment from '@mui/material/InputAdornment';
import { Bs4PopoverModal } from './Bs4ModalTopCenter';
import Bs4MainDispatch from '../Bs4MainDispatch'
import TextField from '@mui/material/TextField';
import * as Bs4Utils from './Bs4Utils';
import {isNullOrNaN} from './Bs4Utils';
import { Typography } from '@mui/material';
import {   
  addSeconds, startOfDay,
} from "date-fns";

const ViewBs4Time = React.forwardRef((props, ref) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const inputRef = React.useRef(null);
  const inputRefEvents = React.useRef({pointerTextField: false});    
  const [error, setError] = React.useState(false);

  const [segment, setSegment] = React.useState(0);
  const [editing, setEditing] = React.useState(null);

  const [minTime, maxTime] = React.useMemo(() => {    
      const hoursMin = parseInt(props.hoursMin);
      const hoursMax = parseInt(props.hoursMax);
      const minTime = Bs4Utils.isNullOrNaN(hoursMin) ? null : addSeconds(startOfDay(new Date()), hoursMin);
      const maxTime = Bs4Utils.isNullOrNaN(hoursMax) ? null : addSeconds(startOfDay(new Date()), hoursMax);
      return [minTime, maxTime];
    }, [props.hoursMin, props.hoursMax]);

  const getViewValue = () => {
    const viewValue = pattern.reduce((valueStr, partPattern) => valueStr + partPattern.prefix+getValueStr(partPattern), '');
    // console.log('...???...ViewValue', segment, viewValue, pattern[0], pattern[1]);
    return viewValue;
  }

  const getNewPattern = (value) => {    
    let minutes = 0;
    let hh = 'hh';
    let mm = 'mm';

    if (!isNullOrNaN(value)) {
      const secoseconds = parseInt(value);
      minutes = parseInt(secoseconds / 60);
      mm = minutes % 60;
      hh = parseInt((minutes - mm) / 60);
    }

    // console.log('...???...NewPattern', value, hh, mm);

    return [      
      // { 
      //   prefix: '',
      //   pattern: 'dd',
      //   maxValue: 31,
      //   value: 'dd',        
      // } , 
      { 
        prefix: '',
        pattern: 'hh',
        maxValue: 9999,
        value: hh,
      } , {
        prefix: ':',
        pattern: 'mm',
        maxValue: 59,
        value: mm,
      }
    ]
  }

  const [pattern, setPattern] = React.useState(() => getNewPattern(props.value));

  const getValueStr = (partPattern) => {
    if (isNullOrNaN(partPattern.value)) partPattern.value = partPattern.pattern;
    let valueStr = String(partPattern.value) ;
    while (valueStr.length < partPattern.pattern.length) valueStr = '0' + valueStr;
    return valueStr;
  }  

  const getValue = () => {
    const hh = parseInt(pattern[0].value);
    const mm = parseInt(pattern[1].value);    
    if (isNullOrNaN(hh) || isNullOrNaN(mm)) return NaN;
    return (hh * 60 + mm) * 60;
  }

  const setValue = () => {
    let newValue = getValue();
    if (isNullOrNaN(newValue)) {
      setPattern([...pattern]);
      setError(true);
    }
    else {
      setPattern(getNewPattern(newValue));    
      setError(false);
    }
    if (props.onChange) props.onChange(newValue);
  }
  
  const setSelectionRange = React.useCallback((segment, pattern) => {
    let start = 0;
    let end = 0;
    for (let i = 0; i < pattern.length; i++) {
      const partPattern = pattern[i];
      if (i >= segment) {
        start += partPattern.prefix.length;
        end = start + getValueStr(partPattern).length;
        break;
      }
      start += partPattern.prefix.length +  getValueStr(partPattern).length;
    }

    if (inputRef.current) inputRef.current.setSelectionRange(start, end);
  }, []);  

  React.useEffect(() => {
    setSelectionRange(segment, pattern);
  }, [segment, pattern, setSelectionRange]);

  const handlePointerUp = (event) => {
    const char = event.target.selectionStart;
    let start = 0;
    let end = 0;
    for (let i = 0; i < pattern.length; i++) {
      const partPattern = pattern[i];
      start += end+partPattern.prefix.length;
      end = start + getValueStr(partPattern).length;        
      if (char >= start && char <= end) {
        setSegment(i);
        setSelectionRange(i, pattern);
      }
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose(event);
      return;
    }

    if (event.key === 'Tab') {
      handleClose(event);
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const partPattern = pattern[segment];

    switch(event.key) {

      case 'ArrowLeft':
        if (segment > 0) setSegment(segment - 1);
        break;

      case 'ArrowRight':
        if (segment < pattern.length - 1) setSegment(segment + 1);
        break;

      case 'Backspace':        
        partPattern.value = partPattern.pattern;
        setValue();        
        break;        
    }    
    
    if (event.key >= '0' && event.key <= '9') {
      let value = (editing === segment) ? parseInt(partPattern.value) || 0 : 0;
      partPattern.value = parseInt(value+event.key);
      if (partPattern.value > partPattern.maxValue) partPattern.value = event.key;
      if (parseInt(partPattern.value+'0') > partPattern.maxValue && segment < pattern.length - 1) setSegment(segment + 1);      
      setValue();
      setEditing(segment);
    }
  }

  const getValueDigitalClock = () => {    
    let date = new Date(0);    
    if (!isNullOrNaN(pattern[0].value)) date.setHours(parseInt(pattern[0].value % 24));
    if (!isNullOrNaN(pattern[1].value)) date.setMinutes(parseInt(pattern[1].value % 60));
    return date;
  }

  const handleChangeDigitalClock = (newValue) => {
    pattern[0].value = String(newValue.getHours());
    pattern[1].value = String(newValue.getMinutes());
    handleClose();
    setValue();    
  }
  
  const handleClear = (event) => {
    pattern[0].value = String(0);
    pattern[1].value = String(0);
    handleClose();
    setValue();    
  }  

  const handleOpen = (event) => {
    if (props.onOpen) props.onOpen();
  }

  const handleClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
    if (!inputRefEvents.current.pointerTextField) if (props.onLeaveField) props.onLeaveField();
    if (props.onClose) props.onClose();
  }

  const handleClickPopper = (event) => {
    event?.stopPropagation();
    if (anchorEl) handleClose();
    setAnchorEl(inputRef.current);
  }  

  const handleKeyDownEsc = (event) => {
    if (event.key === 'Escape') handleClose(event);
  }

  const handleFocusInput = (event) => {
    setEditing(null);
  }

  const handleBlurInput = (event) => {  
    setEditing(null);  
    if (!inputRefEvents.current.pointerTextField) {
      if (props.onLeaveField) props.onLeaveField();
    }
  }

  const disableUnderline = (props.readOnly && !(props.variant && props.variant !== 'standard')) ? {disableUnderline: true} : {};

  const {slotProps} = props;
  const {textField: textFieldAll, popper} = slotProps || {};
  const {InputProps, inputProps, ...textField} = textFieldAll || {};
  const [xx, xxXX] = Bs4MainDispatch.useBs4Locale();

  if (props.readOnly) return (
    <FormControl fullWidth error={props.error} >
      {props.label ? <FormLabel>{props.label}</FormLabel> : null}
      <Typography>{getViewValue()}</Typography>
      <FormHelperText>{props.helperText}</FormHelperText>
    </FormControl> 
  )

  return (
    <React.Fragment>
      <TextField
        ref={ref}        
        inputRef={node => {
          inputRef.current = node;
          if (props.inputRef) props.inputRef.current = node;
        }}
        fullWidth={props.fullWidth}
        label={props.label}
        error={props.error || error}
        helperText={props.helperText}
        value={getViewValue()}
        onKeyDown={handleKeyDown}
        slotProps={{
          htmlInput: {...inputProps},
          input: {
            ...InputProps,
            ...disableUnderline,
            onFocus: handleFocusInput,
            onBlur: handleBlurInput,
            onPointerEnter:(e) => inputRefEvents.current.pointerTextField = true,
            onPointerLeave:(e) => inputRefEvents.current.pointerTextField = false,
            onPointerUp: handlePointerUp, 
            endAdornment:(
              <InputAdornment position="end">
                <IconButton edge='end' onClick={handleClickPopper}>
                  <AccessTimeIcon/>
                </IconButton>
              </InputAdornment>
            )
          }          
        }}         
      />
            
      <Bs4PopoverModal 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onOpen={handleOpen} 
        onClose={handleClose} 
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onKeyDown={handleKeyDownEsc}
      >
        <DigitalClock
          autoFocus  
          disabled={props.disabled}
          value={getValueDigitalClock()}          
          timeStep={props.timeStep} 
          // disableIgnoringDatePartForTimeValidation
          onChange={handleChangeDigitalClock}
          skipDisabled
          minTime={minTime}
          maxTime={maxTime}
        />
        <Stack direction="row" justifyContent='flex-end' alignItems="center" spacing={1} sx={{p: 1}}>
          <Button onClick={handleClear}>{xxXX.components.MuiLocalizationProvider.defaultProps.localeText.clearButtonLabel}</Button>
        </Stack>
      </Bs4PopoverModal>
    </React.Fragment>
  );
});

export const getFormatedTime = (secoseconds) => {  
  let hh = 'hh';
  let mm = 'mm';

  if (!isNullOrNaN(secoseconds)) {
    const minutes = parseInt(secoseconds / 60);
    mm = String(minutes % 60);
    hh = String(parseInt((minutes - mm) / 60));
  }

  while (hh.length < 2) hh = '0' + hh;
  while (mm.length < 2) mm = '0' + mm;
  return hh+':'+mm;
}
export default ViewBs4Time;
